import React, { useId, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { filterProjectsByClients, filterUsersByTeams } from './reportUtils.js';
import { presetRange, RANGE_PRESETS } from './useReportFilters.js';
import { DateRangePicker } from '../controls/DateRangePicker.jsx';
import { ResetFiltersButton } from '../controls/ResetFiltersButton.jsx';
import { fmtDur } from '../../lib/time.js';
import {
  toggleWorkFilter,
  WORK_TYPES,
  workFilterIds,
} from '../../lib/workTypes.js';

export function ReportFilters({
  data,
  isMyReport,
  clientIds,
  projectIds,
  teamIds,
  userIds,
  onClientToggle,
  onProjectToggle,
  onTeamToggle,
  onUserToggle,
  ...range
}) {
  const visibleProjects = filterProjectsByClients(data.allProjects, clientIds);
  const visibleUsers = filterUsersByTeams(data.users, teamIds);

  return (
    <section className="report-filter-panel">
      <ReportRangeControls {...range} />

      <div className="report-filter-grid">
        <FilterGroup
          title="Clients"
          items={data.clients}
          selected={clientIds}
          onToggle={onClientToggle}
        />
        <FilterGroup
          title="Projects"
          items={visibleProjects}
          selected={projectIds}
          onToggle={onProjectToggle}
          label={(project) => `${project.code} ${project.name}`}
        />
        {!isMyReport && (
          <FilterGroup
            title="Teams"
            items={data.teams}
            selected={teamIds}
            onToggle={onTeamToggle}
          />
        )}
        {!isMyReport && (
          <FilterGroup
            title="People"
            items={visibleUsers}
            selected={userIds}
            onToggle={onUserToggle}
          />
        )}
      </div>
    </section>
  );
}

export function ReportRangeFilter(props) {
  return (
    <section className="report-filter-panel report-range-only">
      <ReportRangeControls {...props} />
    </section>
  );
}

// Work type filter: the four work types every hour falls in, each with the
// hours it holds in the period under every other filter (summary
// .workTypeHours), so what an unticked type hides is still on screen. The
// swatches match the hour boxes above it.
function ReportRangeControls({
  from,
  to,
  billing,
  workTypeHours,
  activePreset,
  activeCount,
  onRangeChange,
  onBillingChange,
  onClear,
}) {
  const workTypeId = useId();
  const kept = workFilterIds(billing);
  return (
    <div className="report-filter-top">
      <div className="report-range">
        <div className="report-filter-title">Date range</div>
        <DateRangePicker
          from={from}
          to={to}
          onChange={onRangeChange}
          presets={RANGE_PRESETS}
          presetRange={presetRange}
          activePreset={activePreset}
        />
      </div>
      <div className="billing-filter">
        <div className="report-filter-title" id={workTypeId}>
          Work type
        </div>
        <div
          className="radio-list work-type-list"
          role="group"
          aria-labelledby={workTypeId}
        >
          {WORK_TYPES.map(({ id, label }) => {
            const checked = kept.includes(id);
            return (
              <label key={id} className="check">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={checked && kept.length === 1}
                  onChange={() =>
                    onBillingChange(toggleWorkFilter(billing, id))
                  }
                />
                <i className={`hours-swatch is-${id}`} aria-hidden="true" />
                {label}
                {workTypeHours && (
                  <span className="work-type-hours">
                    {fmtDur(workTypeHours[id] || 0)}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>
      <ResetFiltersButton count={activeCount} onReset={onClear} />
    </div>
  );
}

function FilterGroup({
  title,
  items,
  selected,
  onToggle,
  label = (item) => item.name,
}) {
  const [query, setQuery] = useState('');
  const filteredItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => label(item).toLowerCase().includes(needle));
  }, [items, label, query]);
  const selectedItems = useMemo(
    () => items.filter((item) => selected.includes(item.id)),
    [items, selected],
  );

  // Nothing to filter by — a group with an empty list (a plain user has no
  // clients, a team manager no projects) is noise, not a control.
  if (!items.length) return null;

  return (
    <div className="filter-group">
      <div className="filter-group-head">
        <span>{title}</span>
        <strong>{selected.length || 'All'}</strong>
      </div>
      <label className="filter-search">
        <Search size={14} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${title.toLowerCase()}`}
        />
      </label>
      {/* Folded until the grid is hovered or keyboard-focused, so the panel
          stays short and the table below it is in view (reports.css). */}
      <div className="filter-group-body">
        <div className="filter-group-body-inner">
          <div className="filter-selected-row" aria-label={`Selected ${title}`}>
            <span className="filter-selected-label">Selected</span>
            <div className="filter-selected-chips">
              {selectedItems.length ? (
                selectedItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="filter-selected-chip"
                    aria-label={`Remove ${label(item)}`}
                    data-tooltip={`Remove ${label(item)}`}
                    onClick={() => onToggle(item.id)}
                  >
                    <span>{label(item)}</span>
                    <X size={11} aria-hidden="true" />
                  </button>
                ))
              ) : (
                <span className="filter-selected-empty">All</span>
              )}
            </div>
          </div>
          <div className="filter-list">
            {filteredItems.map((item) => {
              const isSelected = selected.includes(item.id);
              return (
                <label
                  key={item.id}
                  className={`check${isSelected ? ' is-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(item.id)}
                  />
                  <span className="truncate">{label(item)}</span>
                </label>
              );
            })}
            {!filteredItems.length && (
              <span className="filter-empty">No matches</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
